/** @jsx jsx */
import { jsx, css } from "@emotion/react";
import React from "react";
import {
  ItemsTable,
  ReceiptTemplate,
} from "../../../Utils/Template/ReceiptTemplate";
import { tableCell } from "../../../styles/tableStyles";
import { formatDigits } from "../../../Utils/formatNumbers";
import { dateStringTimeConvert } from "../../../Utils/titleCaseFunction";
import { useQuery } from "react-query";
import axios from "axios";
import config from "../../../Helpers/config.json";

// ==============================
//  STYLES
// ==============================
const containerWide = css`
  width: 100%;
  max-width: 1200px;     /* Expand overall width */
  margin: 0 auto;
`;

const wordBreak = css`
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
`;

const tableWrapper = css`
  width: 100%;
  td {
    padding: 8px 12px;
    font-size: 14px;
  }

  tr:nth-of-type(even) {
    background: #fafafa;
  }
`;

const headingRow = css`
  display: grid;
  grid-template-columns: 2fr 3fr 1.2fr 1.2fr;  /* Expanded column widths */
  font-weight: bold;
  padding: 10px 0;
  margin-bottom: 10px;
  border-bottom: 2px solid #444;
  font-size: 16px;
`;

// ==============================
//  HEADERS
// ==============================
const headData = [
  { value: "Account" },
  { value: "Description" },
  { value: "Debit" },
  { value: "Credit" },
];

// ==============================
//  COMPONENT
// ==============================
const SpecificJournalTemplate = ({ selectedJournalID }) => {

  const journalQuery = useQuery(
    ["journalQuery", selectedJournalID],
    async () => {
      const response = await axios.get(
        `${config.epharmUrl}/accounting/journal_id?journalid=${selectedJournalID}`
      );
      return response?.data?.data?.[0];
    },
    { retry: 1 }
  );

  const journal = journalQuery.isSuccess ? journalQuery.data : {};

  // ==============================
  //  HEADER DETAILS
  // ==============================
  const customerDetails = (
    <tbody>
      <tr>
        <td data-testid="journal_id">
          Journal#: <strong>{journal?.journal_id}</strong>
        </td>

        <td data-testid="journal_date">
          Date:{" "}
          <strong>
            {journal?.date ? dateStringTimeConvert(journal.date) : ""}
          </strong>
        </td>
      </tr>
    </tbody>
  );

  // ==============================
  //  ITEM TABLE + HEADING
  // ==============================
  const heading = (
    <div css={headingRow}>
      <div>Account</div>
      <div>Description</div>
      <div>Debit</div>
      <div>Credit</div>
    </div>
  );

  const itemDetails = (
    <div css={containerWide}>
      {heading}

      <ItemsTable headData={headData} css={tableWrapper}>
        <tbody>
          {journal?.details?.length > 0 ? (
            journal.details.map((detail, idx) => (
              <tr key={idx}>
                <td css={wordBreak}>{detail.accountname}</td>
                <td css={wordBreak}>{detail.description}</td>
                <td css={wordBreak}>{formatDigits(detail.debit)}</td>
                <td css={wordBreak}>{formatDigits(detail.credit)}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="4" align="center">
                No journal details available
              </td>
            </tr>
          )}

          <tr>
            <td css={tableCell} colSpan="4"></td>
          </tr>

          <tr>
            <td colSpan="2" align="right">
              <strong>Total Credit:</strong>
            </td>
            <td colSpan="2">
              <strong>{formatDigits(journal?.total_credit)}</strong>
            </td>
          </tr>

          <tr>
            <td colSpan="2" align="right">
              <strong>Total Debit:</strong>
            </td>
            <td colSpan="2">
              <strong>{formatDigits(journal?.total_debit)}</strong>
            </td>
          </tr>
        </tbody>
      </ItemsTable>
    </div>
  );

  return (
    <ReceiptTemplate
      customerDetails={customerDetails}
      itemDetails={itemDetails}
    />
  );
};

export { SpecificJournalTemplate };
